import { persistReducer, Storage } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {
  QntmConfigurationActionTypes,
  QntmConfigurationActions,
  QntmConfigurationState,
  Component,
  ShippingDate,
  WarrantyExtension,
} from './ShopQntmSystemConfigurationModel'
import { ProductGeneralType } from 'src/Libs/shop/types'

const initialState: QntmConfigurationState = {
  items: [
    {
      type: ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR,
      quantity: 1,
      label: '',
      desiredShippingDate: {
        day: null,
        month: null,
        year: null,
      },
      configuration: {
        configuratorId: null,
        components: [],
        warrantyExtension: null,
      },
    },
  ],
}

const qntmConfigurationReducer = (state = initialState, action: QntmConfigurationActions): QntmConfigurationState => {
  switch (action.type) {
    case QntmConfigurationActionTypes.AddComponent: {
      const newComponents: Component[] = [...state.items[0].configuration.components, action.payload]

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: newComponents,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.RemoveComponent: {
      const newComponents = state.items[0].configuration.components.filter(component => component.id !== action.payload)

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: newComponents,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.AddProduct: {
      const { componentId, productId, qty } = action.payload
      const existingComponentIndex = state.items[0].configuration.components.findIndex(component => component.id === componentId)

      if (existingComponentIndex !== -1) {
        const updatedComponents = state.items[0].configuration.components.map((component, index) => {
          if (index === existingComponentIndex) {
            return {
              ...component,
              products: [
                ...component.products,
                {
                  id: productId,
                  qty,
                },
              ],
            }
          }

          return component
        })

        return {
          ...state,
          items: [
            {
              ...state.items[0],
              configuration: {
                ...state.items[0].configuration,
                components: updatedComponents,
              },
            },
          ],
        }
      }

      const newComponent: Component = {
        id: componentId,
        products: [
          {
            id: productId,
            qty,
          },
        ],
      }

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: [...state.items[0].configuration.components, newComponent],
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.RemoveProduct: {
      const { componentId, productId } = action.payload

      const updatedComponents = state.items[0].configuration.components.map(component => {
        if (component.id === componentId) {
          return {
            ...component,
            products: component.products.filter(product => product.id !== productId),
          }
        }

        return component
      })

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: updatedComponents,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.IncreaseQuantity: {
      const { componentId, productId } = action.payload

      const updatedComponents = state.items[0].configuration.components.map(component => {
        if (component.id === componentId) {
          return {
            ...component,
            products: component.products.map(product =>
              product.id === productId
                ? {
                    ...product,
                    qty: product.qty + 1,
                  }
                : product
            ),
          }
        }

        return component
      })

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: updatedComponents,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.DecreaseQuantity: {
      const { componentId, productId } = action.payload

      const updatedComponents = state.items[0].configuration.components.map(component => {
        if (component.id === componentId) {
          return {
            ...component,
            products: component.products.map(product =>
              product.id === productId
                ? {
                    ...product,
                    qty: Math.max(0, product.qty - 1),
                  }
                : product
            ),
          }
        }

        return component
      })

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              components: updatedComponents,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.SetQuantity:
      return {
        ...state,
        items: [
          {
            ...state.items[0],
            quantity: action.payload,
          },
        ],
      }

    case QntmConfigurationActionTypes.UpdateLabel:
      return {
        ...state,
        items: [
          {
            ...state.items[0],
            label: action.payload,
          },
        ],
      }

    case QntmConfigurationActionTypes.SetShippingDate: {
      const newDate: ShippingDate = {
        ...state.items[0].desiredShippingDate,
        day: action.payload.day,
        month: action.payload.month,
        year: action.payload.year,
      }

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            desiredShippingDate: newDate,
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.SetConfiguratorId:
      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              configuratorId: action.payload,
            },
          },
        ],
      }

    case QntmConfigurationActionTypes.AddWarrantyExtension: {
      const newWarrantyExtension: WarrantyExtension = {
        id: action.payload.id,
      }

      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              warrantyExtension: newWarrantyExtension,
            },
          },
        ],
      }
    }

    case QntmConfigurationActionTypes.RemoveWarrantyExtension:
      return {
        ...state,
        items: [
          {
            ...state.items[0],
            configuration: {
              ...state.items[0].configuration,
              warrantyExtension: null,
            },
          },
        ],
      }

    case QntmConfigurationActionTypes.AddConfigurator:
      return {
        ...state,
        items: [action.payload],
      }

    case QntmConfigurationActionTypes.ResetConfigurator:
      return initialState

    default:
      return state
  }
}

const persistConfig: { key: string; storage: Storage; whitelist: (keyof QntmConfigurationState)[] } = {
  key: 'QntmConfiguration',
  storage,
  whitelist: ['items'],
}

export default persistReducer(persistConfig, qntmConfigurationReducer)
